import React, { forwardRef, useContext, useMemo, useState } from "react";
import { SwivuCombo } from "./SwivuCombo.jsx";
import { getSavedBoardSelection } from "../hooks/useBoards.jsx";
import { getIsArchivedForBoard } from "../../other/utils/otherUtils.jsx";
import BryntumContext from "../context/bryntumContext.jsx";
import { classNameHelper } from "../../other/utils/utils.jsx";
import { useConfirm } from "@omit/react-confirm-dialog";

export const SwivuDuplicateEntityHelper = forwardRef(({
  editorReactState,
  record,
  store,
  name,
  optionsFilterFn,
  placeholder,
  onChange,
  error,
  disabled,
  editorRecordKey = "record",
  ...props
}, ref) => {
  const { editorHandler, project } = useContext(BryntumContext);
  const [isCombo, setIsCombo] = useState(record.isPhantom);
  const confirm = useConfirm();

  const allRecordOptions = useMemo(() => {
    const selectedBoard = getSavedBoardSelection();
    const canShowArchivedRecords = getIsArchivedForBoard(selectedBoard.slug);
    return store.query((record) => !record.isPhantom && record[name] && !record.isLinked && (canShowArchivedRecords || !record.archived) && (optionsFilterFn ? optionsFilterFn(record) : true), true);
  }, [record, store, optionsFilterFn]);

  const getRecordOptionById = (id) => {
    return allRecordOptions.find((record) => record.id === id);
  }

  const showWarningDialog = async (recordToUpdate) => {
    const { swivuCurrentUser } = project.boardUsersStore;
    const { name: userName } = project.boardUsersStore.find((boardUserRecord) => boardUserRecord.userId === swivuCurrentUser.id);
    await confirm({
      title: "Heads Up",
      description: `Hey ${userName}. You just selected "${recordToUpdate[name]}". Watch out. When you click Save, changes will be saved to this existing record.`,
      confirmText: "OK",
      cancelButton: null,
      alertDialogOverlay: {
        className: "z-[11002]"
      },
      alertDialogContent: {
        className: "z-[11002]"
      }
    });
  };

  const handlExistingRecordSelect = (recordToUpdate) => {
    const existingOpenedEditor = editorHandler.getEditors().find((editor) => {
      return (editor.id !== editorReactState.id && editor.type === editorReactState.type && editor.recordIdObj.id === record.id);
    });
    editorHandler.updateEditor(editorReactState.id, { record: {...editorReactState.record, [editorRecordKey]: recordToUpdate}, recordIdObj: recordToUpdate }, (editor) => editor.id !== existingOpenedEditor?.id);
  }

  return (
    <>
      <SwivuCombo
        ref={ref}
        {...props}
        onChange={(value) => {
          if (!isCombo) {
            onChange({ value });
            return;
          }

          /* A new record is selected */
          const selectedRecord = getRecordOptionById(value); // Value is the id of selected record
          handlExistingRecordSelect(selectedRecord);
          showWarningDialog(selectedRecord);
          setIsCombo(false);
          onChange({ value: selectedRecord[name], record: selectedRecord });
        }}
        isCombo={isCombo}
        options={allRecordOptions.map((record) => ({
          label: record[name],
          value: record.id,
          className: classNameHelper(record.archived && "line-through", '!min-w-0 truncate')
        }))}
        placeholder={placeholder}
        createOnUnmatched={(value) => {
          onChange({ value });
          setIsCombo(false);
        }}
        resetOnCreateOnUnmatched={false}
        error={error}
        disabled={disabled}
      />
    </>
  );
}
)
